import {
  isRunningInIosNativeApp,
  isRunningInNativeApp,
} from '~/utils/browserInfo';

/**
 * Plataforma e bloqueio (ENG-824).
 *
 * DUAS REGRAS governam este arquivo:
 *
 * 1. A PRECEDÊNCIA MORA SÓ NO CORE. Ele manda as três flags já resolvidas
 *    (`global ligada ? global : item`); aqui não se reimplementa nada disso — só se
 *    responde "em qual das três eu estou agora" e se lê a flag correspondente. Duas
 *    implementações da mesma precedência divergiriam no primeiro ajuste.
 *
 * 2. A PLATAFORMA MORA SÓ NO FRONT. É por isso que o core manda flags e não um veredito
 *    pronto: quem é desktop e quem é mobile é o enquadramento da tela NAQUELE INSTANTE,
 *    e girar o celular ou estreitar a janela muda a resposta sem nova requisição.
 */

/** O que viaja no header. Quatro valores para três flags — ver `currentPlatform`. */
export type RequestPlatform =
  | 'web_desktop'
  | 'web_mobile'
  | 'app_ios'
  | 'app_android';

/** O eixo das flags, igual ao do core. */
export type PlatformFlags = {
  desktop: boolean;
  mobile: boolean;
  app: boolean;
};

/** Ausência de flags é LIBERAÇÃO (spec P2 AC9): contrato antigo não bloqueia nada. */
export const ALL_PLATFORMS_OPEN: PlatformFlags = {
  desktop: true,
  mobile: true,
  app: true,
};

/**
 * O bloco cru do core virado em flags — ou `null`, que todo gate lê como LIBERADO.
 *
 * É A ÚNICA PORTA por onde `platforms` entra no app, e a peneira é o que impede o front
 * de INVENTAR bloqueio: um bloco parcial (`{ app: true }`), um array ou `"true"` como
 * string passariam direto num `?? null`, e aí `isBlocked` responderia `true` para a chave
 * que faltou enquanto `availablePlatformLabels` voltaria vazio — uma parede sem CTA e sem
 * saída, montada a partir de um payload torto.
 *
 * Torto vira `null` e não `ALL_PLATFORMS_OPEN` por um motivo: `null` é a mesma resposta do
 * contrato antigo, então o caminho degradado já existe e está testado em runtime.
 */
export function parsePlatformFlags(raw: unknown): PlatformFlags | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const { desktop, mobile, app } = raw as Record<string, unknown>;

  if (
    typeof desktop !== 'boolean' ||
    typeof mobile !== 'boolean' ||
    typeof app !== 'boolean'
  ) {
    return null;
  }

  return { desktop, mobile, app };
}

/**
 * Em qual plataforma o jogador está agora.
 *
 * APP É `isRunningInNativeApp()`, E SÓ ELE — a ponte do React Native. Navegador in-app do
 * Instagram ou do Facebook é MOBILE WEB, não app: foi exatamente essa confusão que o
 * ENG-843 desfez ao renomear o helper que mentia, e usar um detector de "não é aba comum"
 * casaria com qualquer WKWebView.
 *
 * iOS × Android só refina o VALOR do header (telemetria e suporte); os dois leem a mesma
 * flag `app`. Quem separa de verdade é `web_mobile` × `web_desktop`, que são flags
 * diferentes — e saem do BREAKPOINT, não do aparelho: o header carrega o mesmo critério
 * que pintou a tela, então o core nunca discorda da parede que o jogador está vendo.
 */
export function currentPlatform(isDesktop: boolean): RequestPlatform {
  if (isRunningInNativeApp()) {
    return isRunningInIosNativeApp() ? 'app_ios' : 'app_android';
  }

  return isDesktop ? 'web_desktop' : 'web_mobile';
}

/** A flag que esta plataforma consulta. iOS e Android caem na mesma. */
export function platformFlagKey(
  platform: RequestPlatform,
): keyof PlatformFlags {
  if (platform === 'app_ios' || platform === 'app_android') {
    return 'app';
  }

  return platform === 'web_mobile' ? 'mobile' : 'desktop';
}

/**
 * A feature está bloqueada aqui?
 *
 * `flags` ausente (contrato antigo, payload truncado) libera — o front nunca inventa um
 * bloqueio que o core não mandou.
 */
export function isBlocked(
  flags: PlatformFlags | null | undefined,
  platform: RequestPlatform,
): boolean {
  if (!flags) {
    return false;
  }

  return flags[platformFlagKey(platform)] !== true;
}

/**
 * As plataformas liberadas, por rótulo, na ordem do eixo — o insumo da copy da parede,
 * que NOMEIA onde dá para usar em vez de só dizer que aqui não dá.
 *
 * Pode voltar vazio: o backoffice recusa as três desmarcadas, mas uma escrita direta no
 * banco chega aqui, e a parede trata esse caso sem prometer caminho nenhum.
 */
export function availablePlatformLabels(
  flags: PlatformFlags | null | undefined,
): string[] {
  const resolved = flags ?? ALL_PLATFORMS_OPEN;

  return (
    // "no/na" nos três, e não "pelo": a mesma lista monta a frase com e sem o
    // "somente" ("disponível SOMENTE no aplicativo", "disponível no computador ou
    // no aplicativo"), e "somente pelo aplicativo" lia pior que as duas.
    (
      [
        ['desktop', 'no computador'],
        ['mobile', 'no navegador do celular'],
        ['app', 'no aplicativo'],
      ] as const
    )
      // `=== true` e não truthiness, igual a `isBlocked` e `appIsAvailable`: três leituras
      // da mesma flag não podem discordar na mesma tela. Com o `parsePlatformFlags` na
      // borda não chega mais valor não-booleano aqui, mas a divergência era a do módulo
      // consigo mesmo.
      .filter(([key]) => resolved[key] === true)
      .map(([, label]) => label)
  );
}

/** O app está entre as liberadas? É o que decide se a parede leva o CTA de download. */
export function appIsAvailable(
  flags: PlatformFlags | null | undefined,
): boolean {
  return (flags ?? ALL_PLATFORMS_OPEN).app === true;
}
